import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { reportError } from 'utils/error'
import { UseMutationOptions } from 'react-query/types/react/types'
import { VoiceData, VoiceParams } from 'hooks/useBrandVoices'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Variables {
  voice_params?: Partial<VoiceParams>
  color?: string
}

interface Props {
  id: number
}

type Data = VoiceData

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const updateBrandVoice = async ({
  workspaceId,
  id,
  ...variables
}: Props & Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_voices/${id}`, {
    method: 'PATCH',
    body: {
      ...variables,
    },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to update brand voice', {
      metaData: {
        id,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }

  return response.data
}

export const useUpdateBrandVoice = ({ id }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  return useMutation<Data, unknown, Variables>(
    ['workspaces', workspaceId, 'brandVoices', id],
    async (variables) => {
      const result = await updateBrandVoice({ workspaceId, id, ...variables })
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandVoices', 'list'])
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])
      return result
    },
    {
      ...options,
    },
  )
}
